import { useState, useEffect } from "preact/hooks"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { api_fetch } from "@/api"
import { toast } from "@/components/ui/use-toast"
import { Plus, X } from "lucide-react"
import { formatDistributor, useAsyncEffect } from "@/utils"

interface DistributorChoice {
  name: string
  value: string
}

export interface RecommendationRule {
  source?: string
  dc_region?: string
  dc_codes?: string[]
  product_type?: string
}

/// Format a recommendation rule into a human-readable string
export function formatRecommendationRule(rule: RecommendationRule): string {
  const parts: string[] = []
  
  if (rule.source) {
    parts.push(`${formatDistributor(rule.source)} orders`)
  }
  
  if (rule.dc_region) {
    parts.push(`in the ${rule.dc_region} region`)
  }
  
  if (rule.dc_codes && rule.dc_codes.length > 0) {
    if (rule.dc_codes.length === 1) {
      parts.push(`shipping from ${rule.dc_codes[0]} warehouse`)
    } else {
      parts.push(`shipping from ${rule.dc_codes.join(", ")} warehouses`)
    }
  }
  
  if (rule.product_type) {
    parts.push(`for ${rule.product_type} products`)
  }
  
  if (parts.length === 0) {
    return "Used for all orders"
  }
  
  return `Used for ${parts.join(" ")}`
}

/** Build source dropdown options from org distributors, keeping any already-selected value visible. */
export function sourceChoicesForRule(
  selectedSource: string | undefined,
  distributors: DistributorChoice[],
): DistributorChoice[] {
  if (!selectedSource) return distributors
  if (distributors.some((d) => d.value === selectedSource)) return distributors
  return [
    { value: selectedSource, name: formatDistributor(selectedSource) },
    ...distributors,
  ]
}

export type ProviderDocuments = "bol_pod" | "exit_pass"

/** Label for a provider's document kind, as the settings list, the request dialog and the badge show it. */
export function provider_documents_label(documents: ProviderDocuments | undefined): string {
  return documents === "exit_pass" ? "Exit pass" : "BOL and POD"
}

export interface LogisticsProvider {
  id?: string
  name: string
  email: string
  contact_name?: string
  details?: string
  cc_emails?: string[]
  access_method?: "portal" | "email"
  portal_url?: string
  portal_username?: string
  portal_password?: string
  recommendation_rules?: RecommendationRule[]
  /** What a request to this provider asks for: BOL and POD (default) or the warehouse exit pass. */
  documents?: ProviderDocuments
  is_recommended?: boolean
  recommendation_reason?: string
}

interface LogisticsProviderFormProps {
  isOpen: boolean
  onClose: () => void
  onSave: (providers: LogisticsProvider[]) => void
  existingProviders?: LogisticsProvider[]
  editingProvider?: LogisticsProvider | null
  editingIndex?: number | null
  title?: string
  description?: string
}

export function LogisticsProviderForm({ 
  isOpen, 
  onClose, 
  onSave, 
  existingProviders = [],
  editingProvider = null,
  editingIndex = null,
  title = "Add Logistics Provider",
  description = "Create a new logistics provider to send requests to."
}: LogisticsProviderFormProps) {
  const [provider, setProvider] = useState<LogisticsProvider>({
    name: "",
    email: "",
    contact_name: "",
    details: "",
    cc_emails: [],
    access_method: "email",
    portal_url: "",
    portal_username: "",
    portal_password: "",
    recommendation_rules: [],
    documents: "bol_pod",
  })
  const [loading, setLoading] = useState(false)
  const [showRecommendationRules, setShowRecommendationRules] = useState(false)
  const [distributorChoices, setDistributorChoices] = useState<DistributorChoice[]>([])

  // Same source list as the global customer filter on the deductions table
  useAsyncEffect(async () => {
    if (!isOpen) return
    const response = await api_fetch<DistributorChoice[]>("/deduction/distributors", { use_cache: true })
    if (response.ok && response.value.data) {
      setDistributorChoices(response.value.data)
    }
  }, [isOpen])

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (editingProvider) {
        // Populate form with editing provider data
        setProvider({ ...editingProvider })
      } else {
        // Reset to empty form
        setProvider({
          name: "",
          email: "",
          contact_name: "",
          details: "",
          cc_emails: [],
          access_method: "email",
          portal_url: "",
          portal_username: "",
          portal_password: "",
          recommendation_rules: [],
          documents: "bol_pod",
        })
      }
      setShowRecommendationRules(false)
    }
  }, [isOpen, editingProvider])

  function updateField(field: keyof LogisticsProvider, value: string | string[] | RecommendationRule[] | ProviderDocuments) {
    setProvider({ ...provider, [field]: value })
  }

  function addRecommendationRule() {
    const newRule: RecommendationRule = {
      source: undefined,
      dc_region: undefined,
      dc_codes: [],
      product_type: undefined
    }
    updateField("recommendation_rules", [...(provider.recommendation_rules || []), newRule])
  }

  function removeRecommendationRule(index: number) {
    const rules = [...(provider.recommendation_rules || [])]
    rules.splice(index, 1)
    updateField("recommendation_rules", rules)
  }

  function updateRecommendationRule(index: number, field: keyof RecommendationRule, value: string | string[] | undefined) {
    const rules = [...(provider.recommendation_rules || [])]
    rules[index] = { ...rules[index], [field]: value }
    updateField("recommendation_rules", rules)
  }

  function updateDcCodes(index: number, value: string) {
    const codes = value.split(",").map((code: string) => code.trim()).filter((code: string) => code.length > 0)
    updateRecommendationRule(index, "dc_codes", codes.length > 0 ? codes : undefined)
  }

  async function handleSave() {
    // Validate required fields
    if (!provider.name.trim() || !provider.email.trim()) {
      toast({
        title: "Missing Required Fields",
        description: "Please enter both company name and email address.",
        variant: "destructive",
      })
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(provider.email)) {
      toast({
        title: "Invalid Email Address",
        description: "Please enter a valid email address.",
        variant: "destructive",
      })
      return
    }

    // Validate portal fields if access_method is portal
    const isPortal = provider.access_method === "portal"
    if (isPortal) {
      if (!provider.portal_url || !provider.portal_url.trim()) {
        toast({
          title: "Missing Portal URL",
          description: "Portal URL is required when access method is Portal.",
          variant: "destructive",
        })
        return
      }
    }

    setLoading(true)
    try {
      // Clean up the provider data - convert empty strings to undefined for optional fields
      const cleanedProvider: LogisticsProvider = {
        ...provider,
        contact_name: provider.contact_name?.trim() || undefined,
        details: provider.details?.trim() || undefined,
        cc_emails: (provider.cc_emails || []).length > 0 ? provider.cc_emails : undefined,
        access_method: provider.access_method || "email",
        documents: provider.documents === "exit_pass" ? "exit_pass" : undefined,
        portal_url: isPortal ? (provider.portal_url?.trim() || undefined) : undefined,
        portal_username: isPortal ? (provider.portal_username?.trim() || undefined) : undefined,
        portal_password: isPortal ? (provider.portal_password?.trim() || undefined) : undefined,
        recommendation_rules: (() => {
          const rules = provider.recommendation_rules || []
          if (rules.length === 0) return undefined
          const cleaned = rules.map((rule: RecommendationRule) => ({
            source: rule.source?.trim() || undefined,
            dc_region: rule.dc_region?.trim() || undefined,
            dc_codes: (rule.dc_codes || []).length > 0 ? rule.dc_codes : undefined,
            product_type: rule.product_type?.trim() || undefined,
          })).filter((rule: RecommendationRule) => rule.source || rule.dc_region || rule.dc_codes || rule.product_type)
          return cleaned.length > 0 ? cleaned : undefined
        })()
      }

      let updatedProviders: LogisticsProvider[]
      
      if (editingProvider !== null && editingIndex !== null) {
        // Update existing provider
        updatedProviders = [...existingProviders]
        updatedProviders[editingIndex] = cleanedProvider
      } else {
        // Add new provider
        updatedProviders = [...existingProviders, cleanedProvider]
      }
      
      const response = await api_fetch("/organization/logistics-providers", {
        method: "PUT",
        body: { logistics_providers: updatedProviders },
      })

      if (!response.ok) {
        throw new Error(response.error?.message || "Failed to save logistics provider")
      }

      toast({
        title: "Success",
        description: editingProvider ? "Logistics provider updated successfully" : "Logistics provider added successfully",
      })

      onSave(updatedProviders)
      onClose()
    } catch (error) {
      console.error("Error saving logistics provider:", error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save logistics provider",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  function handleCancel() {
    onClose()
  }

  const isPortal = provider.access_method === "portal"

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent class="max-w-2xl max-h-[90vh]" style={{ display: "flex", flexDirection: "column" }}>
        <DialogHeader class="shrink-0 pb-4">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div class="flex-1 overflow-y-auto min-h-0 -mx-6 px-6">
          <div class="space-y-4">
          {!showRecommendationRules && (
            <>
          <div>
            <Label htmlFor="name">Company Name *</Label>
            <Input
              id="name"
              value={provider.name}
              onChange={(e) => updateField("name", e.currentTarget.value)}
              placeholder="e.g., ABC Logistics"
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={provider.email}
              onChange={(e) => updateField("email", e.currentTarget.value)}
              placeholder="e.g., contact@abclogistics.com"
            />
          </div>

          <div>
            <Label htmlFor="access_method">Access Method *</Label>
            <Select 
              value={provider.access_method || "email"} 
              onValueChange={(value: "portal" | "email") => updateField("access_method", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select access method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="portal">Portal</SelectItem>
              </SelectContent>
            </Select>
            <p class="text-xs text-gray-500 mt-1">
              {isPortal 
                ? "Portal access requires a website URL and credentials" 
                : "Email access uses the existing email workflow"}
            </p>
          </div>

          {!isPortal && (
            <div>
              <Label htmlFor="documents">Documents to request *</Label>
              <Select
                value={provider.documents || "bol_pod"}
                onValueChange={(value: ProviderDocuments) => updateField("documents", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select documents" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bol_pod">BOL and POD (carrier or 3PL)</SelectItem>
                  <SelectItem value="exit_pass">Exit pass (warehouse or co-packer gate pass)</SelectItem>
                </SelectContent>
              </Select>
              <p class="text-xs text-gray-500 mt-1">
                {provider.documents === "exit_pass"
                  ? "Requests ask this contact for the exit pass issued at pickup, not a BOL or POD"
                  : "Requests ask this contact for the Bill of Lading and Proof of Delivery"}
              </p>
            </div>
          )}

          {isPortal && (
            <div class="space-y-4 p-4 bg-gray-50 rounded-lg border">
              <h4 class="font-medium text-sm">Portal Credentials</h4>
              
              <div>
                <Label htmlFor="portal_url">Portal URL *</Label>
                <Input
                  id="portal_url"
                  type="url"
                  value={provider.portal_url || ""}
                  onChange={(e) => updateField("portal_url", e.currentTarget.value)}
                  placeholder="https://portal.example.com"
                />
              </div>

              <div>
                <Label htmlFor="portal_username">Portal Username (Optional)</Label>
                <Input
                  id="portal_username"
                  value={provider.portal_username || ""}
                  onChange={(e) => updateField("portal_username", e.currentTarget.value)}
                  placeholder="Your portal username"
                />
              </div>

              <div>
                <Label htmlFor="portal_password">Portal Password (Optional)</Label>
                <Input
                  id="portal_password"
                  type="password"
                  value={provider.portal_password || ""}
                  onChange={(e) => updateField("portal_password", e.currentTarget.value)}
                  placeholder="Your portal password"
                />
                <p class="text-xs text-gray-500 mt-1">Only visible to system administrators</p>
              </div>
            </div>
          )}

          {!isPortal && (
            <>
              <div>
                <Label htmlFor="contact">Contact Name (Optional)</Label>
                <Input
                  id="contact"
                  value={provider.contact_name || ""}
                  onChange={(e) => updateField("contact_name", e.currentTarget.value)}
                  placeholder="e.g., John Smith"
                />
              </div>
              
              <div>
                <Label htmlFor="details">Details / Order Types (Optional)</Label>
                <Input
                  id="details"
                  value={provider.details || ""}
                  onChange={(e) => updateField("details", e.currentTarget.value)}
                  placeholder="e.g., Frozen foods, Dry goods, Produce"
                />
              </div>
              
              <div>
                <Label htmlFor="cc">CC Emails (Optional)</Label>
                <Input
                  id="cc"
                  value={(provider.cc_emails || []).join(", ")}
                  onChange={(e) => {
                    const emails = e.currentTarget.value.split(",").map((email: string) => email.trim()).filter((email: string) => email);
                    updateField("cc_emails", emails);
                  }}
                  placeholder="e.g., logistics@yourbrand.com, shipping@yourbrand.com"
                />
                <p class="text-xs text-gray-500 mt-1">Separate multiple emails with commas</p>
              </div>
            </>
          )}
          </>
          )}

          {!editingProvider && (
            <div class="border-t pt-4">
              <div class="flex items-center justify-between mb-2">
                <Label>Order Type Rules (Optional)</Label>
                <Button
                  type="button"
                  variant="outline"
                  // @ts-ignore
                  size="sm"
                  onClick={() => {
                    setShowRecommendationRules(!showRecommendationRules)
                    if (!showRecommendationRules && (provider.recommendation_rules || []).length === 0) {
                      addRecommendationRule()
                    }
                  }}
                >
                  {showRecommendationRules ? "Hide" : "Show"} Rules
                </Button>
              </div>
              <p class="text-xs text-gray-500 mb-3">
                Configure when this provider should be recommended based on source, DC location, etc.
              </p>

              {showRecommendationRules && (
                <div class="space-y-4">
                  {(provider.recommendation_rules || []).map((rule: RecommendationRule, index: number) => (
                    <div key={index} class="p-4 border rounded-lg bg-white space-y-3">
                      <div class="flex items-center justify-between">
                        <h5 class="font-medium text-sm">Rule {index + 1}</h5>
                        <Button
                          type="button"
                          variant="ghost"
                          // @ts-ignore
                          size="sm"
                          onClick={() => removeRecommendationRule(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      <div class={rule.source === "unfi" ? "grid grid-cols-2 gap-3" : ""}>
                        <div>
                          <Label htmlFor={`rule-source-${index}`}>Source (Optional)</Label>
                          <Select
                            value={rule.source || ""}
                            onValueChange={(value) => {
                              // Update source and clear dc_region if not UNFI in one update
                              const rules = [...(provider.recommendation_rules || [])]
                              rules[index] = { 
                                ...rules[index], 
                                source: value || undefined,
                                dc_region: value === "unfi" ? rules[index].dc_region : undefined
                              }
                              updateField("recommendation_rules", rules)
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select source" />
                            </SelectTrigger>
                            <SelectContent>
                              {sourceChoicesForRule(rule.source, distributorChoices).map((choice) => (
                                <SelectItem key={choice.value} value={choice.value}>
                                  {choice.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {rule.source === "unfi" && (
                          <div>
                            <Label htmlFor={`rule-dc-region-${index}`}>DC Region (Optional)</Label>
                            <Select
                              value={rule.dc_region || ""}
                              onValueChange={(value) => updateRecommendationRule(index, "dc_region", value || undefined)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select region" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="East">East</SelectItem>
                                <SelectItem value="West">West</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>

                      <div>
                        <Label htmlFor={`rule-dc-codes-${index}`}>DC Codes (Optional)</Label>
                        <Input
                          id={`rule-dc-codes-${index}`}
                          value={(rule.dc_codes || []).join(", ")}
                          onChange={(e) => updateDcCodes(index, e.currentTarget.value)}
                          placeholder="e.g., ATL, RAC, SAR (comma-separated)"
                        />
                        <p class="text-xs text-gray-500 mt-1">Specific warehouse codes (comma-separated)</p>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={addRecommendationRule}
                    class="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Recommendation Rule
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
        </div>

        <DialogFooter class="mt-4 shrink-0 border-t pt-4">
          <Button variant="outline" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? (editingProvider ? "Saving..." : "Adding...") : (editingProvider ? "Save Changes" : "Add Provider")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("formatRecommendationRule", () => {
    it("formats known sources with display names", () => {
      expect(formatRecommendationRule({ source: "walmart" })).toBe("Used for Walmart orders")
      expect(formatRecommendationRule({ source: "unfi" })).toBe("Used for UNFI orders")
    })

    it("returns all-orders text when rule has no constraints", () => {
      expect(formatRecommendationRule({})).toBe("Used for all orders")
    })
  })

  describe("sourceChoicesForRule", () => {
    const distributors = [
      { name: "KeHE", value: "kehe" },
      { name: "UNFI", value: "unfi" },
      { name: "Walmart", value: "walmart" },
    ]

    it("returns org distributors as-is when no selection", () => {
      expect(sourceChoicesForRule(undefined, distributors)).toEqual(distributors)
    })

    it("keeps a selected source visible even if missing from org list", () => {
      expect(sourceChoicesForRule("ahold", distributors)).toEqual([
        { value: "ahold", name: "Ahold" },
        ...distributors,
      ])
    })

    it("does not duplicate a selected source already in the list", () => {
      expect(sourceChoicesForRule("walmart", distributors)).toEqual(distributors)
    })
  })
}
